import { useCallback, useState } from 'react';
import { TextInput, type TextInputProps } from './TextInput';

type CurrencyInputProps = Omit<TextInputProps, 'prefix' | 'keyboardType' | 'value' | 'onChangeText'> & {
  /** Raw numeric string value (e.g. "1500.50") */
  value: string;
  /** Called with raw numeric string on change */
  onChangeText: (raw: string) => void;
};

/** Strip everything except digits and a single decimal point. */
function sanitize(text: string): string {
  let result = '';
  let hasDecimal = false;
  for (const ch of text) {
    if (ch >= '0' && ch <= '9') {
      result += ch;
    } else if (ch === '.' && !hasDecimal) {
      result += '.';
      hasDecimal = true;
    }
  }
  return result;
}

/** Format a numeric string with commas (e.g. "1500" → "1,500"). */
function formatWithCommas(raw: string): string {
  if (!raw) return '';
  const [intPart, decPart] = raw.split('.');
  if (!intPart) return raw;
  const formatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return decPart !== undefined ? `${formatted}.${decPart}` : formatted;
}

export function CurrencyInput({ value, onChangeText, ...rest }: CurrencyInputProps) {
  const [displayValue, setDisplayValue] = useState(() => formatWithCommas(value));

  const handleChangeText = useCallback(
    (text: string) => {
      // Strip commas and non-numeric chars
      const raw = sanitize(text.replace(/,/g, ''));
      setDisplayValue(formatWithCommas(raw));
      onChangeText(raw);
    },
    [onChangeText],
  );

  // Keep display in sync if value changes externally
  const formatted = formatWithCommas(value);
  const shown = formatted === formatWithCommas(sanitize(displayValue.replace(/,/g, '')))
    ? displayValue
    : formatted;

  return (
    <TextInput
      prefix="$"
      keyboardType="numeric"
      value={shown}
      onChangeText={handleChangeText}
      {...rest}
    />
  );
}
